import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { AddressLookup } from '../../components/AddressLookup';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import {
  Building2, LogIn, UserPlus, Shield, Eye, EyeOff, CheckCircle, AlertTriangle,
} from 'lucide-react';
import { PORTAL_API, COUNTRIES, US_STATES } from './constants';

export function SupplierLogin({ branding, logoSrc, portalTitle, Footer, onViewChange, onLoginSuccess }) {
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) { toast.error('Email and password required'); return; }
    setLoading(true);
    try {
      const r = await api.post(`${PORTAL_API}/login`, { email: loginEmail, password: loginPassword });
      if (r.data.requires_totp_setup) {
        onViewChange('totp_setup', { totpUri: r.data.totp_uri, totpSupplierId: r.data.supplier_id });
      } else if (r.data.requires_totp) {
        onViewChange('totp_verify', { tempToken: r.data.temp_token });
      } else if (r.data.requires_password_change) {
        onViewChange('password_change', { pwTempToken: r.data.temp_token });
        toast.warning(r.data.message);
      } else if (r.data.access_token) {
        onLoginSuccess(r.data.access_token, r.data.supplier);
      }
    } catch (e) { toast.error(getApiErrorMessage(e, 'Login failed')); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {logoSrc ? (
            <img src={logoSrc} alt={branding.company_name} className="h-14 mx-auto mb-3 object-contain" />
          ) : (
            <Building2 className="h-12 w-12 text-white mx-auto mb-3" />
          )}
          <h1 className="text-2xl font-bold text-white">{portalTitle}</h1>
          <p className="text-slate-400 text-sm mt-1">Sign in to access bids, contracts, and documentation</p>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Email Address</Label>
              <Input value={loginEmail} onChange={e => setLoginEmail(e.target.value)} type="email" placeholder="supplier@company.com"
                onKeyDown={e => e.key === 'Enter' && handleLogin()} className="h-10" data-testid="supplier-login-email" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Password</Label>
              <div className="relative">
                <Input value={loginPassword} onChange={e => setLoginPassword(e.target.value)} type={showPassword ? 'text' : 'password'}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()} className="h-10 pr-10" data-testid="supplier-login-password" />
                <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-muted-foreground" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button className="w-full h-10" onClick={handleLogin} disabled={loading} data-testid="supplier-login-btn">
              <LogIn className="h-4 w-4 mr-2" /> {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <div className="text-right">
              <button onClick={() => onViewChange('forgot_password')} className="text-xs text-indigo-600 hover:underline" data-testid="supplier-forgot-password-link">
                Forgot your password?
              </button>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-xs text-muted-foreground">Don't have an account?</p>
            <Button variant="link" className="text-sm" onClick={() => onViewChange('register')} data-testid="supplier-register-link">
              <UserPlus className="h-3.5 w-3.5 mr-1" /> Register as a New Supplier
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

export function SupplierForgotPassword({ onViewChange }) {
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleForgotPassword = async () => {
    if (!forgotEmail) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await api.post(`${PORTAL_API}/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('If an account exists with that email, a password reset link has been sent.');
    } catch { toast.success('If an account exists with that email, a password reset link has been sent.'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold mb-1">Reset Your Password</h2>
          {forgotSent ? (
            <div>
              <p className="text-sm text-muted-foreground mb-4">If an account exists with that email address, you will receive a password reset link shortly.</p>
              <Button className="w-full h-10" onClick={() => { onViewChange('login'); }}>
                Back to Login
              </Button>
            </div>
          ) : (
            <div className="space-y-3 text-left">
              <p className="text-xs text-muted-foreground text-center mb-2">Enter your email address and we'll send you a link to reset your password.</p>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Email Address</Label>
                <Input value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} type="email"
                  placeholder="your@email.com" className="h-10" onKeyDown={e => e.key === 'Enter' && handleForgotPassword()} />
              </div>
              <Button className="w-full h-10" onClick={handleForgotPassword} disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <Button variant="ghost" className="w-full text-xs" onClick={() => onViewChange('login')}>
                Back to Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SupplierRegister({ branding, logoSrc, Footer, onViewChange }) {
  const [loading, setLoading] = useState(false);
  const [regForm, setRegForm] = useState({
    email: '', password: '', confirm_password: '', company_name: '', contact_name: '',
    phone: '', address: '', city: '', state: '', postal_code: '', country: '', website: '',
    business_type: '', tax_id: '', sic_code: '', sic_description: '', duns_number: '', description: '',
  });
  const [sicSearch, setSicSearch] = useState('');
  const [sicResults, setSicResults] = useState([]);
  const [sicOpen, setSicOpen] = useState(false);

  const rf = (field, value) => setRegForm(p => ({ ...p, [field]: value }));

  const searchSic = async (q) => {
    setSicSearch(q);
    if (q.length < 2) { setSicResults([]); return; }
    try {
      const r = await api.get(`/api/system/sic-codes?search=${encodeURIComponent(q)}`);
      setSicResults(r.data.data || []);
      setSicOpen(true);
    } catch { setSicResults([]); }
  };

  const selectSic = (code, desc) => {
    rf('sic_code', code);
    rf('sic_description', desc);
    setSicSearch(`${code} — ${desc}`);
    setSicOpen(false);
  };

  const handleRegister = async () => {
    if (regForm.password !== regForm.confirm_password) { toast.error('Passwords do not match'); return; }
    if (!regForm.email || !regForm.password || !regForm.company_name || !regForm.contact_name) {
      toast.error('Please fill in all required fields'); return;
    }
    setLoading(true);
    try {
      const r = await api.post(`${PORTAL_API}/register`, regForm);
      toast.success(r.data.message);
      onViewChange('login');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Registration failed')); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-6">
          {logoSrc ? (
            <img src={logoSrc} alt={branding.company_name} className="h-12 mx-auto mb-2 object-contain" />
          ) : (
            <Building2 className="h-10 w-10 text-white mx-auto mb-2" />
          )}
          <h1 className="text-xl font-bold text-white">{branding.company_name ? `${branding.company_name} — Supplier Registration` : 'Supplier Registration'}</h1>
          <p className="text-slate-400 text-xs mt-1">Complete the form below to register as a supplier</p>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-red-500">Company Name *</Label>
              <Input value={regForm.company_name} onChange={e => rf('company_name', e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-red-500">Contact Name *</Label>
              <Input value={regForm.contact_name} onChange={e => rf('contact_name', e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-red-500">Email *</Label>
              <Input type="email" value={regForm.email} onChange={e => rf('email', e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Phone</Label>
              <Input value={regForm.phone} onChange={e => rf('phone', e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-red-500">Password *</Label>
              <Input type="password" value={regForm.password} onChange={e => rf('password', e.target.value)} className="h-9 text-sm" placeholder="Min 12 chars, upper+lower+number+special" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-red-500">Confirm Password *</Label>
              <Input type="password" value={regForm.confirm_password} onChange={e => rf('confirm_password', e.target.value)} className="h-9 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="col-span-2 space-y-1">
              <Label className="text-xs font-semibold">Address Lookup (US & UK)</Label>
              <AddressLookup onSelect={addr => {
                rf('address', addr.address || regForm.address);
                rf('city', addr.city || regForm.city);
                rf('state', addr.state_province || regForm.state);
                rf('postal_code', addr.zip_postal || regForm.postal_code);
                if (addr.country) rf('country', addr.country);
              }} placeholder="Start typing your address..." />
            </div>
            <div className="space-y-1"><Label className="text-xs">Address</Label><Input value={regForm.address} onChange={e => rf('address', e.target.value)} className="h-9 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">City</Label><Input value={regForm.city} onChange={e => rf('city', e.target.value)} className="h-9 text-sm" /></div>
            <div className="space-y-1">
              <Label className="text-xs">State / Province</Label>
              {regForm.country === 'United States' ? (
                <select value={regForm.state} onChange={e => rf('state', e.target.value)}
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                  <option value="">Select state...</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <Input value={regForm.state} onChange={e => rf('state', e.target.value)} className="h-9 text-sm" />
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Country</Label>
              <select value={regForm.country} onChange={e => rf('country', e.target.value)}
                className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                <option value="">Select country...</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Zip / Postal Code</Label><Input value={regForm.postal_code} onChange={e => rf('postal_code', e.target.value)} className="h-9 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Website</Label><Input value={regForm.website} onChange={e => rf('website', e.target.value)} className="h-9 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Tax ID / VAT Number</Label><Input value={regForm.tax_id} onChange={e => rf('tax_id', e.target.value)} className="h-9 text-sm" /></div>
            <div className="space-y-1 relative">
              <Label className="text-xs">SIC Code</Label>
              <Input value={sicSearch || (regForm.sic_code ? `${regForm.sic_code} — ${regForm.sic_description}` : '')}
                onChange={e => searchSic(e.target.value)}
                onFocus={() => { if (sicResults.length) setSicOpen(true); }}
                className="h-9 text-sm" placeholder="Search by code or industry..." />
              {sicOpen && sicResults.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-xl max-h-40 overflow-y-auto">
                  {sicResults.map(s => (
                    <button key={s.code} onClick={() => selectSic(s.code, s.description)}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent/30 flex items-center gap-2 border-b last:border-b-0">
                      <span className="font-semibold text-primary w-12">{s.code}</span>
                      <span>{s.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1"><Label className="text-xs">D&B (DUNS) Number</Label><Input value={regForm.duns_number} onChange={e => rf('duns_number', e.target.value.replace(/\D/g, '').slice(0, 9))} className="h-9 text-sm" placeholder="9-digit DUNS number" maxLength={9} /></div>
          </div>
          <div className="space-y-1 mb-4">
            <Label className="text-xs">Business Description</Label>
            <Textarea value={regForm.description} onChange={e => rf('description', e.target.value)} rows={2} className="text-sm" placeholder="Brief description of your products/services" />
          </div>
          <div className="flex gap-3">
            <Button className="flex-1 h-10" onClick={handleRegister} disabled={loading}>
              <UserPlus className="h-4 w-4 mr-2" /> {loading ? 'Submitting...' : 'Submit Registration'}
            </Button>
            <Button variant="outline" className="h-10" onClick={() => onViewChange('login')}>Back to Login</Button>
          </div>
          <p className="text-[10px] text-muted-foreground text-center mt-3">Your registration will be reviewed and you'll be notified by email once approved.</p>
        </div>
        <Footer />
      </div>
    </div>
  );
}

export function SupplierTotpSetup({ totpUri, totpSupplierId, onLoginSuccess }) {
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleTotpSetup = async () => {
    if (!totpCode || totpCode.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const r = await api.post(`${PORTAL_API}/2fa/setup`, { supplier_id: totpSupplierId, code: totpCode });
      onLoginSuccess(r.data.access_token);
      toast.success('Two-factor authentication enabled!');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Invalid code')); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
          <Shield className="h-12 w-12 text-blue-600 mx-auto mb-3" />
          <h2 className="text-lg font-bold mb-1">Set Up Two-Factor Authentication</h2>
          <p className="text-xs text-muted-foreground mb-4">Scan the QR code below with your authenticator app (Google Authenticator, Microsoft Authenticator, etc.)</p>
          <div className="bg-muted/30 rounded-xl p-4 mb-4">
            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpUri)}`} alt="TOTP QR Code" className="mx-auto" />
            <p className="text-[10px] text-muted-foreground mt-2">If you cannot scan the QR code, contact your administrator for manual setup assistance.</p>
          </div>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Enter the 6-digit code from your app</Label>
              <Input value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="h-10 text-center text-lg tracking-widest" maxLength={6} placeholder="000000"
                onKeyDown={e => e.key === 'Enter' && handleTotpSetup()} data-testid="totp-setup-code" />
            </div>
            <Button className="w-full h-10" onClick={handleTotpSetup} disabled={loading || totpCode.length !== 6}>
              <CheckCircle className="h-4 w-4 mr-2" /> {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SupplierTotpVerify({ tempToken, onLoginSuccess }) {
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleTotpVerify = async () => {
    if (!totpCode || totpCode.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const r = await api.post(`${PORTAL_API}/2fa/verify`, { temp_token: tempToken, code: totpCode });
      onLoginSuccess(r.data.access_token);
      toast.success('Logged in successfully');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Invalid code')); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
          <Shield className="h-12 w-12 text-blue-600 mx-auto mb-3" />
          <h2 className="text-lg font-bold mb-1">Two-Factor Verification</h2>
          <p className="text-xs text-muted-foreground mb-4">Enter the 6-digit code from your authenticator app</p>
          <div className="space-y-3">
            <Input value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="h-12 text-center text-2xl tracking-widest" maxLength={6} placeholder="000000"
              onKeyDown={e => e.key === 'Enter' && handleTotpVerify()} data-testid="totp-verify-code" />
            <Button className="w-full h-10" onClick={handleTotpVerify} disabled={loading || totpCode.length !== 6}>
              <LogIn className="h-4 w-4 mr-2" /> {loading ? 'Verifying...' : 'Verify & Sign In'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SupplierPasswordChange({ pwTempToken, onViewChange }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async () => {
    if (newPassword !== confirmNewPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const r = await api.post(`${PORTAL_API}/change-password`, { temp_token: pwTempToken, new_password: newPassword });
      toast.success(r.data.message);
      onViewChange('login');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-center mb-1">Password Expired</h2>
          <p className="text-xs text-muted-foreground text-center mb-4">Your password has expired. Please set a new one.</p>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs font-semibold">New Password</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="h-10" placeholder="Min 12 chars, upper+lower+number+special" /></div>
            <div className="space-y-1"><Label className="text-xs font-semibold">Confirm New Password</Label>
              <Input type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} className="h-10" /></div>
            <Button className="w-full h-10" onClick={handlePasswordChange} disabled={loading}>
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
